import { FormEvent, useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { EditorialPage, handwritten } from "../components/editorial";
import { captureConversion } from "../lib/analytics";
import {
  ChoiceInput,
  Honeypot,
  ScaleInput,
  TextInput,
  segmentToEnum,
  submitSurvey,
  usageAmountToEnum,
} from "../components/survey/SurveyInputs";

// /survey — LL-0184 beta feedback form, SHORT variant. Unlisted: never linked
// from nav, only reached via a per-tester link (?g=A|C|D&n=<first name>) sent
// directly by Adrien/Josh. Group B (live UAT) is verbal/in-person per LL-0156,
// no web form. This is the "in a rush" fallback next to /survey/full (the
// 25-question long form) — every question here is optional, same as there.
//
// Design brief: one scrolling page (not a multi-step wizard — these are short
// enough that steps just add taps), tap-friendly 1-5 scales with labelled
// endpoints so every tester reads the same scale the same way, and the 3
// "core" metrics (ease / satisfaction / referral) tagged so results roll up
// across groups even though the surrounding questions differ per group.

type ScoreField =
  "ease_score" | "satisfaction_score" | "referral_score" | "return_score";

type Question =
  | { id: string; kind: "text"; prompt: string }
  | {
      id: string;
      kind: "scale";
      prompt: string;
      lowLabel: string;
      highLabel: string;
      score: ScoreField;
      followUp?: { id: string; prompt: string; rows?: number };
    }
  | {
      id: string;
      kind: "choice";
      prompt: string;
      options: string[];
      followUp?: { id: string; prompt: string; rows?: number };
    };

// Asked in every group, ahead of the group-specific questions — segmentation
// (who's answering) + usage-amount (how much they've actually used the app),
// so every score below is read against actual exposure instead of assumed
// exposure. Plus the PMF benchmark question (Sean Ellis test) and a
// dedicated bug/crash report, kept separate from general confusion since a
// crash is an engineering ticket, not a UX opinion. See
// world-class-survey-review-2026-07-05.md for the reasoning behind all four.
const SEGMENT_Q: Question = {
  id: "SEG1",
  kind: "choice",
  prompt: "Which of these is closest to you right now?",
  options: [
    "New to London",
    "Have a friend group but want more to do",
    "Struggle to make plans happen",
    "Just curious",
  ],
};

const USAGE_Q: Question = {
  id: "USE1",
  kind: "choice",
  prompt:
    "Since you got the app, how many times would you say you've opened it?",
  options: ["Just the once", "A handful of times", "Regularly"],
};

const PMF_Q: Question = {
  id: "PMF1",
  kind: "choice",
  prompt: "How would you feel if you could no longer use LetsLoop?",
  options: [
    "Very disappointed",
    "Somewhat disappointed",
    "Not disappointed",
    "N/A, I don't really use it",
  ],
};

const BUG_Q: Question = {
  id: "BUG1",
  kind: "choice",
  prompt:
    "Did you run into any bugs, crashes, freezes, or things that just didn't work?",
  options: [
    "No, nothing like that",
    "Yes, something didn't work right",
    "Not sure / can't remember",
  ],
  followUp: {
    id: "BUG1b",
    prompt: "(Optional) What happened, and what were you doing at the time?",
    // Longer than the other followUp placeholders (Why?/Why or why not?) —
    // wraps to 2 lines on a phone width, so needs 2 rows or the wrapped
    // second line clips past the box border. Found via a real screenshot
    // (Josh, 2026-07-06), not just typecheck/build.
    rows: 2,
  },
};

const GROUPS: Record<string, { title: string; questions: Question[] }> = {
  A: {
    title: "A few questions about LetsLoop",
    questions: [
      SEGMENT_Q,
      USAGE_Q,
      {
        id: "A1",
        kind: "text",
        prompt:
          "In one sentence: what did you think LetsLoop was for, before you opened it?",
      },
      {
        id: "A2",
        kind: "text",
        prompt: "What did you actually end up doing in it?",
      },
      {
        id: "A3",
        kind: "scale",
        prompt: "How easy was it to do the main thing you wanted to do?",
        lowLabel: "Really hard",
        highLabel: "Effortless",
        score: "ease_score",
      },
      {
        id: "A4",
        kind: "text",
        prompt:
          "Was there anything that confused you, or anything you expected to find but couldn't?",
      },
      {
        id: "A5",
        kind: "scale",
        prompt: "How likely are you to open it again this week?",
        lowLabel: "No chance",
        highLabel: "Definitely",
        score: "return_score",
        followUp: { id: "A5b", prompt: "(Optional) Why or why not?" },
      },
      PMF_Q,
      BUG_Q,
      {
        id: "A6",
        kind: "scale",
        prompt: "How likely are you to tell a friend about it?",
        lowLabel: "No chance",
        highLabel: "Definitely",
        score: "referral_score",
        followUp: { id: "A6b", prompt: "(Optional) What would you tell them?" },
      },
    ],
  },
  C: {
    title: "Quick ones about LetsLoop",
    questions: [
      SEGMENT_Q,
      USAGE_Q,
      {
        id: "C1",
        kind: "text",
        prompt:
          "Quick one before you dive in: last time you wanted to do something social or spontaneous in London, what stopped you or made it hard?",
      },
      {
        id: "C2",
        kind: "text",
        prompt: "What's the first thing you tried to do in the app?",
      },
      {
        id: "C3",
        kind: "choice",
        prompt: "Did it work the way you expected?",
        options: ["Yes", "Kind of", "No"],
      },
      {
        id: "C4",
        kind: "scale",
        prompt: "How easy was it overall?",
        lowLabel: "Really hard",
        highLabel: "Effortless",
        score: "ease_score",
      },
      {
        id: "C5",
        kind: "scale",
        prompt: "Overall, how would you rate the experience?",
        lowLabel: "Poor",
        highLabel: "Excellent",
        score: "satisfaction_score",
      },
      {
        id: "C6",
        kind: "text",
        prompt: "What's the one thing you'd add or fix first?",
      },
      PMF_Q,
      BUG_Q,
      // C7 was double-barrelled ("open it again, or tell a friend") which
      // broke referral_score comparability with A6/D4 and mixed two signals
      // into one number. Split before any responses existed (verified 0 rows
      // in beta_survey_responses at the time of the change, 2026-07-08).
      {
        id: "C7",
        kind: "scale",
        prompt: "How likely are you to open it again this week?",
        lowLabel: "No chance",
        highLabel: "Definitely",
        score: "return_score",
      },
      {
        id: "C8",
        kind: "scale",
        prompt: "How likely are you to tell a friend about it?",
        lowLabel: "No chance",
        highLabel: "Definitely",
        score: "referral_score",
      },
    ],
  },
  D: {
    title: "Two minutes, that's it",
    questions: [
      SEGMENT_Q,
      USAGE_Q,
      {
        id: "D1",
        kind: "choice",
        prompt: "Did the app do what you expected?",
        options: ["Yes", "Kind of", "No"],
      },
      {
        id: "D2",
        kind: "scale",
        prompt: "How would you rate the overall experience?",
        lowLabel: "Poor",
        highLabel: "Excellent",
        score: "satisfaction_score",
        followUp: { id: "D2b", prompt: "(Optional) Why?" },
      },
      {
        id: "D3",
        kind: "text",
        prompt: "What's the one thing you'd add or fix first?",
      },
      PMF_Q,
      BUG_Q,
      {
        id: "D4",
        kind: "scale",
        prompt: "How likely are you to tell a friend about it?",
        lowLabel: "No chance",
        highLabel: "Definitely",
        score: "referral_score",
      },
    ],
  },
};

const SurveyPage = () => {
  const [searchParams] = useSearchParams();
  const group = (searchParams.get("g") || "").trim().toUpperCase();
  const name = (searchParams.get("n") || "").trim();
  // Stable tester identifier, independent of the (free-text) name above — see
  // LL-0184 NORTH-STAR.md. Read once from the URL and submitted unchanged.
  const slug = (searchParams.get("s") || "").trim();

  const config = GROUPS[group];

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [scores, setScores] = useState<Record<ScoreField, number | null>>({
    ease_score: null,
    satisfaction_score: null,
    referral_score: null,
    return_score: null,
  });
  const [honeypot, setHoneypot] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "done" | "error">(
    "idle",
  );

  useEffect(() => {
    document.title = "LetsLoop feedback";
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!config || state === "loading") return;
    setState("loading");
    try {
      const ok = await submitSurvey({
        group,
        formVariant: "short",
        testerName: name,
        testerSlug: slug,
        easeScore: scores.ease_score,
        satisfactionScore: scores.satisfaction_score,
        referralScore: scores.referral_score,
        returnScore: scores.return_score,
        usageAmount: usageAmountToEnum(answers.USE1),
        segment: segmentToEnum(answers.SEG1),
        answers,
        honeypot,
      });
      if (!ok) throw new Error("submit failed");
      setState("done");
      captureConversion("beta_survey_submit", { group, form_variant: "short" });
    } catch {
      setState("error");
    }
  };

  const fullLinkParams = new URLSearchParams();
  if (group) fullLinkParams.set("g", group);
  if (name) fullLinkParams.set("n", name);
  if (slug) fullLinkParams.set("s", slug);
  const fullLinkQuery = fullLinkParams.toString();
  const fullLink = `/beta-review/full${fullLinkQuery ? `?${fullLinkQuery}` : ""}`;

  // No (or an invalid) group code on the link — rather than dead-ending
  // ("this link looks off"), fall back to the full form: it already defaults
  // an empty group to "FULL" server-side, and already collects name +
  // optional email/phone at the end, so a bare /beta-review link (shared
  // without a per-tester ?g=/&n=) still works end to end. Per Adrien
  // 2026-07-06.
  if (!config) {
    return <Navigate to={fullLink} replace />;
  }

  return (
    <EditorialPage>
      <div className="max-w-xl mx-auto px-6 md:px-0 pt-12 pb-24">
        <div className="mb-10">
          <Link to="/" aria-label="LetsLoop home">
            <img
              src="/letsloop-logo.png"
              alt="LetsLoop"
              className="h-7 w-auto"
            />
          </Link>
        </div>

        {state === "done" ? (
          <div className="border-2 border-ink bg-paper-tan p-6 shadow-[6px_6px_0_0_rgba(5,5,5,1)]">
            <p className="font-serif text-2xl text-ink">Thank you.</p>
            <p className="text-gray-700 mt-1">
              Really appreciate you taking the time. It goes straight to the
              founders.
            </p>
          </div>
        ) : (
          <>
            <p
              className="text-2xl md:text-3xl text-salmon mb-2 -rotate-2 origin-left"
              style={handwritten}
            >
              hey{name ? ` ${name}` : ""}
            </p>
            <h1 className="font-serif text-ink tracking-tight leading-[1.05] text-3xl md:text-4xl mb-2">
              {config.title}
            </h1>
            <p className="text-sm text-gray-600 mb-8">
              Nothing here is required, answer whatever you fancy and skip the
              rest.{" "}
              <Link to={fullLink} className="underline hover:text-salmon">
                Got 5 minutes? Take the full version instead
              </Link>
              .
            </p>

            <form onSubmit={submit} className="space-y-8">
              <Honeypot value={honeypot} setValue={setHoneypot} />

              {config.questions.map((q, i) => (
                <div key={q.id}>
                  <label className="block text-ink font-semibold mb-2.5 leading-snug">
                    {i + 1}. {q.prompt}
                  </label>
                  {q.kind === "text" && (
                    <TextInput
                      value={answers[q.id] || ""}
                      onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))}
                    />
                  )}
                  {q.kind === "choice" && (
                    <>
                      <ChoiceInput
                        options={q.options}
                        value={answers[q.id]}
                        onChange={(opt) =>
                          setAnswers((a) => ({ ...a, [q.id]: opt }))
                        }
                      />
                      {q.followUp && (
                        <TextInput
                          value={answers[q.followUp.id] || ""}
                          onChange={(v) =>
                            setAnswers((a) => ({ ...a, [q.followUp!.id]: v }))
                          }
                          rows={q.followUp.rows || 1}
                          placeholder={q.followUp.prompt}
                        />
                      )}
                    </>
                  )}
                  {q.kind === "scale" && (
                    <>
                      <ScaleInput
                        value={scores[q.score]}
                        onChange={(n) =>
                          setScores((s) => ({ ...s, [q.score]: n }))
                        }
                        lowLabel={q.lowLabel}
                        highLabel={q.highLabel}
                      />
                      {q.followUp && (
                        <TextInput
                          value={answers[q.followUp.id] || ""}
                          onChange={(v) =>
                            setAnswers((a) => ({ ...a, [q.followUp!.id]: v }))
                          }
                          rows={q.followUp.rows || 1}
                          placeholder={q.followUp.prompt}
                        />
                      )}
                    </>
                  )}
                </div>
              ))}

              <button
                type="submit"
                disabled={state === "loading"}
                className="w-full h-12 border-2 border-ink bg-ink text-white font-semibold shadow-[4px_4px_0_0_rgba(255,160,122,1)] disabled:opacity-40 disabled:shadow-none transition-opacity"
              >
                {state === "loading" ? "Sending…" : "Send it"}
              </button>
              {state === "error" && (
                <p className="text-sm text-red-600">
                  Something went wrong, mind trying again in a sec?
                </p>
              )}
            </form>
          </>
        )}
      </div>
    </EditorialPage>
  );
};

export default SurveyPage;
